import { unref } from 'vue'
import { isNavigationFailure } from 'vue-router'
import { useCore } from '@/composables/core'
import { useLogger } from '@booksy-com/vue-tools'
import { equals } from 'ramda'
import { useDeeplinksService } from '@/composables/deeplinks'
import { useEmitter } from '@/composables/emitter'
import { useEppo } from '@/composables/eppo'
import flags from '@/lib/flags'
import {
  booksyBillingRedirect,
  deeplinkRedirect,
  defaultRouteRedirect,
  gdprRedirect,
  nativeSubscriptionRedirect,
  onboardingFinishedRedirect,
  onboardingRedirect,
  paymentMethodInfoRedirect,
  richTextEditorRedirect,
  targetUrlRedirect,
  deleteAccountRedirect,
} from './redirects'

import { useRedirection } from './redirection'

import type { RedirectHandler, RedirectRoute } from './types'

const logger = useLogger('default-redirect')

const ORDERED_REDIRECTS: RedirectHandler[] = [
  richTextEditorRedirect,
  onboardingRedirect,
  deleteAccountRedirect,
  booksyBillingRedirect,
  paymentMethodInfoRedirect,
  nativeSubscriptionRedirect,
  gdprRedirect,
  deeplinkRedirect,
  targetUrlRedirect,
  onboardingFinishedRedirect,
]

const defaultRedirect = async ({ onboardingFinished } = { onboardingFinished: false }): Promise<unknown> => {
  const { store, router } = useCore()
  const emitter = useEmitter()
  const eppo = useEppo()
  const { execute } = useDeeplinksService()

  logger.info('default redirect started')

  // redirection query
  const redirection = useRedirection().get() as import('./types').RedirectionPayload | undefined
  const _currentRoute = unref(router.currentRoute)
  const route: RedirectRoute = {
    ..._currentRoute,
    params: {
      ..._currentRoute.params,
      ...(redirection ? { redirection } : {}),
    } as RedirectRoute['params'],
  }
  const query = route.params.redirection?.query || {}
  const params = route.params

  // delete redirection to current route
  if (params.redirection && route.name
    && (equals(`/${params.redirection.path}`, route.path)
    || equals(params.redirection.name, route.name))
  ) {
    useRedirection().clear()
    delete params.redirection
  }

  // IMPORTANT!!!
  // do not add anything unnecessary here
  const context = {
    route,
    query,
    params,
    router,
    store,
    emitter,
    eppo,
    logger,
    executeDeeplink: execute,
    defaultRedirect,
    // TODO to be replaced with storage composable
    flags,
    // TODO stuff to be removed from this context
    onboardingFinished,
  }

  for (const redirect of ORDERED_REDIRECTS) {
    let result: unknown
    try {
      result = await redirect(context)
    } catch (error) {
      // vue-router rejects the in-flight navigation with NAVIGATION_CANCELLED/ABORTED
      // when a newer navigation supersedes it. This is common on mobile (rapid taps,
      // back/forward gestures) and is expected and harmless — stop redirecting.
      // Real errors are rethrown so they still reach error tracking.
      if (isNavigationFailure(error)) {
        logger.debug('navigation superseded during redirect', error)
        return
      }
      throw error
    }
    if (result) {
      await router.isReady()
      return result
    }
  }

  useRedirection().clear()
  return defaultRouteRedirect(context)
}

export function useDefaultRedirect (): typeof defaultRedirect {
  return defaultRedirect
}
